import _ from "lodash"
import { NavLink } from "react-router-dom"
import { useState, useContext, useEffect, useMemo } from "react"
import { useQuery } from "@apollo/client"
import { UserContext } from "../../context/userContext"
import { FirebaseContext } from "../../firebase"
import { LoaderProgressBar } from "../../components/Loaders"
import { GET_ORGANIZATIONS_IDSTAMP } from "./queries"

export default function NavMain(props) {
  const [collpase, setCollapse] = useState(false)
  const [customTemplates, setCustomTemplates] = useState([])
  const [systemTemplates, setSystemTemplates] = useState([])

  const [loading, setLoading] = useState(true)
  const [loadingSystemTemplates, setLoadingSystemTemplates] = useState(true)
  const [loadingCustomTemplates, setLoadingCustomTemplates] = useState(true)
  const [menu, setMenu] = useState([])
  const [parentActive, setParentActive] = useState(null)
  const [organizationIDStamp, setOrganizationIDStamp] = useState(null)
  const userCtx = useContext(UserContext)
  const firebase = useContext(FirebaseContext)

  const combinedTemplates = useMemo(() => {
    // If a template exists in customTemplates, drop the matching system template.
    const customIds = new Set(customTemplates.map((t) => t.template_id))
    const filteredSystem = systemTemplates.filter(
      (t) => !customIds.has(t.template_id)
    )

    // Combine custom & filtered system templates, and sort by template_id
    let _combinedTemplates = [...customTemplates, ...filteredSystem]
    _combinedTemplates = _.orderBy(_combinedTemplates, "template_id")

    return [..._combinedTemplates]
  }, [customTemplates, systemTemplates])

  // Load templates
  const loadSystemTemplates = async () => {
    try {
      // Fetch system templates
      const templateDocsSystem = firebase.db.collection(`system-templates/`)
      const snapshotSystem = await templateDocsSystem.get()

      let templateList = []
      snapshotSystem.docs.forEach((doc) => {
        templateList.push({
          template_id: doc.id,
          ...doc.data(),
        })
      })
      setSystemTemplates(templateList)
    }
    catch (error) {
      console.error("Failed to load system templates:", error)
    }
    finally {
      setLoadingSystemTemplates(false)
    }
  }

  const loadCustomTemplates = async () => {
    try {
      // Fetch custom templates
      const enterpriseId = userCtx.session.user.enterprise_id
      const templateDocsCustom = firebase.db.collection(`enterprise-config/${enterpriseId}/custom-templates/`)
      const snapshotCustom = await templateDocsCustom.get()

      let templateList = []
      snapshotCustom.docs.forEach((doc) => {
        templateList.push({
          template_id: doc.id,
          ...doc.data(),
        })
      })
      setCustomTemplates(templateList)
    }
    catch (error) {
      console.error("Failed to load custom templates:", error)
    }
    finally {
      setLoadingCustomTemplates(false)
    }
  }

  useEffect(() => {
    // Initialize loading states
    setLoading(true)
    setLoadingSystemTemplates(true)
    setLoadingCustomTemplates(true)

    // Load templates
    loadSystemTemplates()
    loadCustomTemplates()

    // Subscription for menuDoc
    const menuDoc = userCtx.session.menu
    const menuUnsubscribe = menuDoc.onSnapshot(
      (docSnapshot) => {
        const menu = docSnapshot.data().menu
        setMenu(menu)
        setLoading(false)
      },
      (err) => {
        console.error(`Encountered error: ${err}`)
      }
    )

    // Cleanup functions
    return () => {
      menuUnsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCtx.session.menu, userCtx.session.ux])

  useQuery(GET_ORGANIZATIONS_IDSTAMP, {
    variables: {
      staff_id: userCtx.session.user?.staff_id,
      organization_id: userCtx.session.user?.organization_id,
    },
    skip: !userCtx.session.user.staff_id,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const selectedOrganizationId = userCtx.session.user.organization_id
      const organizationInContext = _.find(
        data.organizations,
        (org) => org.organization_id === selectedOrganizationId
      )
      if (organizationInContext) {
        setOrganizationIDStamp(organizationInContext.organization_id_stamp)
      } else {
        console.error("No matching organization found")
      }
    },
  })

  function replaceOrgIdInPath(path, orgId) {
    return path.replace("{organization_id}", orgId)
  }

  const handleLevelThird = (items) => {
    return (
      <ul className="menu-inner">
        {_.map(items, (row, i) => {
          if (row.path) {
            return (
              <li key={i}>
                <NavLink
                  className={({ isActive }) => {
                    return isActive ? "active" : null
                  }}
                  to={replaceOrgIdInPath(row.path, organizationIDStamp)} // Modified line
                >
                  {row.icon && (
                    <span className="material-icons">{row.icon}</span>
                  )}
                  {row.label}
                  {!_.isEmpty(row.items) && handleLevelThird(row.items)}
                </NavLink>
              </li>
            )
          } else {
            return (
              <li key={i}>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a href="#" onClick={() => setParentActive(null)}>
                  {row.icon && (
                    <span className="material-icons">{row.icon}</span>
                  )}
                  {row.label}
                  {!_.isEmpty(row.items) && handleLevelThird(row.items)}
                </a>
              </li>
            )
          }
        })}
      </ul>
    )
  }

  const handleLevelSecond = (items, parentFieldId, label) => {
    if (parentFieldId !== parentActive) return null

    const isTemplateAvailable = (filterObj) => {
      // Try to find the template in combinedTemplates (system + custom) 
      const template = _.find(combinedTemplates, filterObj)
      if (template) {
        // If a template is found, check its availability
        return template.template_availability === 1
      }
      // If a template is not found, assume the menu item is available
      return true
    }

    const renderCustomTemplate = (template_category) => {
      const customTmpList = _.filter(customTemplates, {
        template_category,
      })

      return _.map(customTmpList, (row, i) => {
        // Check if the template is available
        if (!isTemplateAvailable({ template_id: row.template_id })) {
          return null
        }

        const handleUrl = () => {
          switch (template_category) {
            case "activity_custom":
              return `/activity-log/${row.template_id}/`
            default:
              return null
          }
        }
        return (
          <li key={`${template_category}_${i}`}>
            <NavLink
              className={({ isActive }) => {
                return isActive ? "active" : null
              }}
              to={handleUrl()}
            >
              <span className="material-icons">book</span>
              {row.template_title}
            </NavLink>
          </li>
        )
      })
    }

    const handleCustomTemplates = () => {
      switch (label) {
        case "Activity Logs":
          return renderCustomTemplate("activity_custom")
        default:
          return null
      }
    }

    return (
      <ul className="menu-dropdown expand">
        {_.map(items, (row, i) => {
          if (!isTemplateAvailable({ template_title: row.label })) {
            return null
          }
          if (row.path) {
            return (
              <li
                key={i}
                className={!_.isEmpty(row.items) ? "menu-dropdown-inner" : null}
              >
                <NavLink
                  className={({ isActive }) => {
                    return isActive ? "active" : null
                  }}
                  title={row.label}
                  exact
                  to={replaceOrgIdInPath(row.path, organizationIDStamp)} // Modified line
                  end
                >
                  {row.icon && (
                    <span className="material-icons">{row.icon}</span>
                  )}
                  <div className="label">{row.label}</div>
                </NavLink>
                {!_.isEmpty(row.items) && handleLevelThird(row.items)}
              </li>
            )
          } else {
            return (
              <li
                key={i}
                className={!_.isEmpty(row.items) ? "menu-dropdown-inner" : null}
                onClick={
                  row.label === "My Availability"
                    ? () =>
                      userCtx.setToggleAppointments({
                        toggle: true,
                        page: "availability",
                      })
                    : null
                }
              >
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a title={row.label}>
                  {row.icon && (
                    <span className="material-icons">{row.icon}</span>
                  )}
                  <div className="label">{row.label}</div>
                </a>
                {!_.isEmpty(row.items) && handleLevelThird(row.items)}
              </li>
            )
          }
        })}
        {handleCustomTemplates()}
      </ul>
    )
  }

  const handleLevelFirst = () => {
    const handleClickParent = (e) => {
      e.target.id === parentActive
        ? setParentActive(null)
        : setParentActive(e.target.id)
    }

    return (
      <ul>
        {_.map(menu, (row, i) => {
          const fieldId = `parent_${i}`
          const handleClassName =
            parentActive === fieldId
              ? "btn-dropdown-toogle expand"
              : "btn-dropdown-toogle"
          return (
            <li key={fieldId}>
              <button
                id={fieldId}
                onClick={handleClickParent}
                className={handleClassName}
              >
                {row.label}
              </button>
              {!_.isEmpty(row.items) &&
                handleLevelSecond(row.items, fieldId, row.label)}
            </li>
          )
        })}
      </ul>
    )
  }

  if (loading || loadingSystemTemplates || loadingCustomTemplates)
    return (
      <nav className="nav-main">
        <LoaderProgressBar />
      </nav>
    )

  return (
    <nav className="nav-main">
      <button
        className="nav-main-toogle"
        onClick={() => {
          props.setToogle(!props.toogle)
          setCollapse(!collpase)
        }}
      >
        Toggle
      </button>

      {handleLevelFirst()}

      <button
        className="btn-logout"
        onClick={() => {
          localStorage.removeItem("hasRedirected")
          firebase.signOut()
        }}
      >
        Logout
      </button>
    </nav>
  )
}
